import {Component, OnInit, ViewChild} from '@angular/core';
import {ConfirmationService, MessageService, TreeNode} from 'primeng/api';
import {TranslocoService} from '@ngneat/transloco';
import {HierarchyService} from "../../services/hierarchy.service";
import {AuthGuard} from "../../common/auth-guard.service";
import {CommonMessagesService} from "../../services/common-messages.service";
import {Organization} from "../../model/organization";
import {OrganizationsService} from "../../services/organizations.service";
import {OrganizationFilters} from "../../model/filters/organization-filters";
import {Geocoding} from "../../model/geocoding";
import {GeocodingService} from "../../services/geocoding.service";
import {OrganizationTypesService} from "../../services/organization-types.service";
import {OrganizationType} from "../../model/organization-type";

@Component({
  selector: 'app-organizations-list',
  templateUrl: './organizations-list.component.html',
  styleUrls: ['./organizations-list.component.scss']
})
export class OrganizationsListComponent implements OnInit {

  @ViewChild('treeTable')
  public treeTable: any;

  public language: string;

  public hierarchy: TreeNode[];
  public hierarchyFilters: TreeNode[];

  public nodes: any;
  public organizationTypes: OrganizationType[];

  public organizationParent: any;
  public countries: { name: string }[];

  public filtersVisible: boolean;
  public currentFilters: OrganizationFilters;

  public isStateDisabled: boolean;
  public isLocalityDisabled: boolean;

  public loadingOrganizations: boolean = true;

  private static conflictHttpError: number = 409;
  private static forbiddenHttpError: number = 403;

  private states: { name: string }[];
  private localities: { name: string }[];
  private geocodings: Geocoding[];
  private countryStates: Map<string, Set<string>>;
  private stateLocalities: Map<string, Set<string>>;


  constructor(private hierarchyService: HierarchyService,
              private messageService: MessageService,
              private i18n: TranslocoService,
              private guard: AuthGuard,
              private confirmationService: ConfirmationService,
              private organizationsService: OrganizationsService,
              private commonMessagesService: CommonMessagesService,
              private geocodingService: GeocodingService,
              private organizationTypesService: OrganizationTypesService,
              public authGuard: AuthGuard) {
    this.currentFilters = new OrganizationFilters();
  }

  ngOnInit(): void {
    this.hierarchy = Array.from({length: 10}, () => {
      return {
        children: [],
        data: {},
        expanded: true,
        label: '',
        parent: null
      }
    });
    this.language = this.i18n.getActiveLang();
    this.loadHierarchyList();
    this.loadFilters();
  }

  private loadFilters(): void {
    this.organizationTypesService.getOrganizationTypes().toPromise().then(response => {
      this.organizationTypes = response.organizationTypes;
    });
    this.loadGeocoding();
  }

  private loadGeocoding(): void {
    this.isStateDisabled = true;
    this.isLocalityDisabled = true;
    this.countryStates = new Map<string, Set<string>>();
    this.stateLocalities = new Map<string, Set<string>>();
    this.geocodingService.getOrganizationGeocodings(this.language).subscribe(response => {
      this.geocodings = response.geocodings;
      let geocodingData = this.geocodingService.getGeocodingData(this.geocodings, this.language);
      this.countryStates = geocodingData.countryStates;
      this.stateLocalities = geocodingData.stateLocalities;
      this.countries = geocodingData.countries;
      this.states = geocodingData.states;
      this.localities = geocodingData.localities;
    });
  }

  private loadHierarchyList(): void {
    this.loadingOrganizations = true;
    this.organizationsService.getOrganizations()
      .subscribe(response => {
        let responseTreeNode = this.hierarchyService.getHierarchyListTreeNode(response.hierarchyListDto);
        this.nodes = responseTreeNode[0];
        this.hierarchy = responseTreeNode[1];
        this.hierarchyFilters = responseTreeNode[2];
      }).add(() => this.loadingOrganizations = false);
  }

  confirmAndDelete(organization: Organization): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      this.deleteOrganization(organization);
    });
  }

  deleteOrganization(organization: Organization): void {
    if (organization.organizationType.root) {
      this.commonMessagesService.showErrorWhileDeletingMessage(OrganizationsListComponent.forbiddenHttpError);
      return;
    } else if (organization.totalMachines > 0 || this.nodes[organization.organizationUuid].children.length > 0) {
      this.commonMessagesService.showErrorWhileDeletingMessage(OrganizationsListComponent.conflictHttpError);
      return;
    }
    this.organizationsService.deleteOrganization(organization.organizationUuid).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadHierarchyList();
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    });
  }

  resetFilters(): void {
    this.currentFilters = new OrganizationFilters();
    this.organizationParent = null;
  }

  applyFilters(): void {
    this.treeTable.filter(this.currentFilters.name?.trim(), 'name', 'contains');
    this.treeTable.filter(this.currentFilters.orgCode?.trim(), 'orgCode', 'contains');
    this.treeTable.filter(this.currentFilters.description?.trim(), 'description', 'contains');
    this.treeTable.filter(this.currentFilters.localities, 'geocoding.locality.' + this.language, 'in');
    this.treeTable.filter(this.currentFilters.states, 'geocoding.state.' + this.language, 'in');
    this.treeTable.filter(this.currentFilters.countries, 'geocoding.country.' + this.language, 'in');
    this.treeTable.filter(this.currentFilters.organizationTypesUuids, 'organizationType.organizationTypeUuid', 'in');
    this.treeTable.filter(this.currentFilters.parentUuid, 'parentUuid', 'is');
    this.filtersVisible = false;
  }

  onCountryChange(event): void {
    if (event.value && event.value.length > 0) {
      this.isStateDisabled = false;
    } else {
      this.isStateDisabled = true;
    }
    this.isLocalityDisabled = true;
    this.currentFilters.states = [];
    this.currentFilters.localities = [];
  }

  onStateChange(event): void {
    if (event.value && event.value.length > 0) {
      this.isLocalityDisabled = false;
    } else {
      this.isLocalityDisabled = true;
    }
    this.currentFilters.localities = [];
  }

  get filteredStates(): { name: string }[] {
    if (this.currentFilters.countries?.length > 0) {
      return this.states.filter(state => {
        for (const country of this.currentFilters.countries) {
          if (this.countryStates.get(country).has(state.name)) {
            return true;
          }
        }
        return false;
      });
    }
    return this.states;
  }

  get filteredLocalities(): { name: string }[] {
    if (this.currentFilters.states?.length > 0) {
      return this.localities.filter(locality => {
        for (const state of this.currentFilters.states) {
          if (this.stateLocalities.get(state).has(locality.name)) {
            return true;
          }
        }
        return false;
      });
    }
    return this.localities;
  }

}
